import React, { createContext, useContext, useEffect, useState } from 'react';
import { StatusCode } from '../../components/Alert.tsx';
import FeedbacksController from '../../controllers/FeedbacksController.ts';
import { Feedback } from '../../models/feedbacks/feedback.ts';
import { FeedbackSearchParam } from '../../models/searchParams/feedbackSearchParam.ts';
import { TableSearchParam } from '../../models/searchParams/tableSearchParam.ts';
import { useAlert } from '../alertProvider.tsx';

type FeedbacksContextType = {
    isLoaded?: boolean;
    feedbacks: Feedback[];
    totalCount: number;
    tableSearchParam: TableSearchParam;
    setTableSearchParam: (tableSearchParam: TableSearchParam) => void;
    searchParam: FeedbackSearchParam;
    setSearchParam: (baseSearchParam: FeedbackSearchParam) => void;
    deleteFeedback: (feedbackID: number) => {};
};

const FeedbacksContext = createContext<FeedbacksContextType>({} as FeedbacksContextType);

export const FeedbacksContextProvider = ({ children }: { children: any }) => {
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [tableSearchParam, setTableSearchParam] = useState<TableSearchParam>({ offset: 0, limit: 25 });
    const [searchParam, setSearchParam] = useState<FeedbackSearchParam>({} as FeedbackSearchParam);
    const { showAlert } = useAlert();

    const fetchFeedbacks = async () => {
        try {
            setIsLoaded(false);
            const result = await FeedbacksController.get(tableSearchParam, searchParam);
            setFeedbacks(result.feedbacks);
            setTotalCount(result.totalCount);
        } catch (error) {
            console.error('Error fetching feedbacks:', error);
        } finally {
            setIsLoaded(true);
        }
    };
    useEffect(() => {
        fetchFeedbacks();
    }, [tableSearchParam, searchParam]);

    const deleteFeedback = async (feedbackID: number) => {
        try {
            const result = await FeedbacksController.delete(feedbackID);
            if (result.notificationStatus == StatusCode.success)
                fetchFeedbacks();
            else
                showAlert(result.notificationStatus, result.notificationMessage);
        } catch (error) {
            console.error('Error deleting feedback:', error);
        } finally {
            setIsLoaded(true);
        }
    }

    const context: FeedbacksContextType = {
        isLoaded,
        feedbacks,
        totalCount,
        tableSearchParam,
        setTableSearchParam,
        searchParam,
        setSearchParam,
        deleteFeedback
    }

    return (
        <FeedbacksContext.Provider value={context}>
            {children}
        </FeedbacksContext.Provider>
    )
}

export const useFeedbacksContext = () => useContext(FeedbacksContext)
